import React, { useState, useEffect } from 'react';
import { 
  Settings, Save, Plus, Trash2, ToggleLeft, 
  DollarSign, X, Check, AlertCircle 
} from 'lucide-react';

function ModelConfig({ onClose }) {
  const [config, setConfig] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModel, setShowAddModel] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');

  useEffect(() => {
    loadConfig();
    loadAvailableModels();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const API_URL = window.API_CONFIG?.API_URL || process.env.REACT_APP_API_URL || 
    (window.location.hostname === 'localhost' 
      ? 'http://localhost:12343' 
      : `${window.location.protocol}//${window.location.hostname.replace('era-classifier', 'era-classifier-api')}`);
  const API_KEY = process.env.REACT_APP_API_KEY || '';

  const loadConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/models`, {
        headers: {
          'X-API-Key': API_KEY
        }
      });
      const data = await response.json();
      // The API returns the config in the correct format already
      setConfig(data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load configuration');
      setLoading(false);
    }
  };

  const loadAvailableModels = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/models/available`, {
        headers: {
          'X-API-Key': API_KEY
        }
      });
      const data = await response.json();
      setAvailableModels(data.models || []);
    } catch (err) {
      console.error('Failed to load available models');
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      // Send the config in the same format as models_config.json
      const response = await fetch(`${API_URL}/api/v1/models/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
        body: JSON.stringify(config),
      });
      
      if (response.ok) {
        setSuccess('Configuration saved successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save configuration');
      }
    } catch (err) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const toggleModel = (modelKey) => {
    setConfig({
      ...config,
      categorization_models: {
        ...config.categorization_models,
        [modelKey]: {
          ...config.categorization_models[modelKey],
          enabled: !config.categorization_models[modelKey].enabled
        }
      }
    });
  };

  const updateModelField = (modelKey, field, value) => {
    setConfig({
      ...config,
      categorization_models: {
        ...config.categorization_models,
        [modelKey]: {
          ...config.categorization_models[modelKey],
          [field]: value
        }
      }
    });
  };

  const deleteModel = (modelKey) => {
    const newModels = { ...config.categorization_models };
    delete newModels[modelKey];
    setConfig({
      ...config,
      categorization_models: newModels
    });
  };

  const addModel = () => {
    if (!selectedModel) return;
    
    const modelInfo = availableModels.find(m => m.id === selectedModel);
    // Use the model ID directly as the key, replacing slashes with dashes for safety
    const modelKey = modelInfo.id.replace(/\//g, '-');
    
    setConfig({
      ...config,
      categorization_models: {
        ...config.categorization_models,
        [modelKey]: {
          provider: modelInfo.provider,
          model_id: modelInfo.id,
          display_name: modelInfo.name,
          enabled: true,
          temperature: 0.3,
          max_tokens: 500,
          cost_per_1k_prompt: parseFloat(modelInfo.pricing?.prompt) || 0.001,
          cost_per_1k_completion: parseFloat(modelInfo.pricing?.completion) || 0.002
        }
      }
    });
    
    setShowAddModel(false);
    setSelectedModel('');
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold flex items-center">
              <Settings className="mr-3" />
              Model Configuration
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
              <AlertCircle className="mr-2" size={20} />
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-700">
              <Check className="mr-2" size={20} />
              {success}
            </div>
          )}

          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Categorization Models</h3>
                <button
                  onClick={() => setShowAddModel(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                >
                  <Plus className="mr-2" size={18} />
                  Add Model
                </button>
              </div>

              <div className="space-y-4">
                {Object.entries(config.categorization_models).map(([key, model], index) => (
                  <div key={key} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center">
                        <button
                          onClick={() => toggleModel(key)}
                          className={`p-2 rounded-lg ${
                            model.enabled ? 'bg-green-500 text-white' : 'bg-gray-300'
                          }`}
                        >
                          <ToggleLeft size={20} />
                        </button>
                        <div className="ml-3">
                          <h4 className="font-medium text-gray-800">
                            Model {index + 1}: {model.display_name || key}
                          </h4>
                        </div>
                      </div>
                      <button
                        onClick={() => deleteModel(key)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Temperature</label>
                        <input
                          type="number"
                          value={model.temperature}
                          onChange={(e) => updateModelField(key, 'temperature', parseFloat(e.target.value))}
                          className="w-full px-3 py-2 border rounded-lg"
                          step="0.1"
                          min="0"
                          max="2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Max Tokens</label>
                        <input
                          type="number"
                          value={model.max_tokens}
                          onChange={(e) => updateModelField(key, 'max_tokens', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border rounded-lg"
                          min="1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          <DollarSign size={14} className="inline" /> /1K Prompt
                        </label>
                        <input
                          type="number"
                          value={model.cost_per_1k_prompt}
                          onChange={(e) => updateModelField(key, 'cost_per_1k_prompt', parseFloat(e.target.value))}
                          className="w-full px-3 py-2 border rounded-lg"
                          step="0.001"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          <DollarSign size={14} className="inline" /> /1K Completion
                        </label>
                        <input
                          type="number"
                          value={model.cost_per_1k_completion}
                          onChange={(e) => updateModelField(key, 'cost_per_1k_completion', parseFloat(e.target.value))}
                          className="w-full px-3 py-2 border rounded-lg"
                          step="0.001"
                          min="0"
                        />
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Model ID: {model.model_id}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Search Models</h3>
              <div className="border rounded-lg p-4 bg-gray-50">
                <h4 className="font-medium text-gray-800 mb-3">Perplexity Configuration</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Temperature</label>
                    <input
                      type="number"
                      value={config.search_models.perplexity.temperature}
                      onChange={(e) => setConfig({
                        ...config,
                        search_models: {
                          ...config.search_models,
                          perplexity: {
                            ...config.search_models.perplexity,
                            temperature: parseFloat(e.target.value)
                          }
                        }
                      })}
                      className="w-full px-3 py-2 border rounded-lg"
                      step="0.1"
                      min="0"
                      max="2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Max Tokens</label>
                    <input
                      type="number"
                      value={config.search_models.perplexity.max_tokens}
                      onChange={(e) => setConfig({
                        ...config,
                        search_models: {
                          ...config.search_models,
                          perplexity: {
                            ...config.search_models.perplexity,
                            max_tokens: parseInt(e.target.value)
                          }
                        }
                      })}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      <DollarSign size={14} className="inline" /> /1K Tokens
                    </label>
                    <input
                      type="number"
                      value={config.search_models.perplexity.cost_per_1k_prompt}
                      onChange={(e) => setConfig({
                        ...config,
                        search_models: {
                          ...config.search_models,
                          perplexity: {
                            ...config.search_models.perplexity,
                            cost_per_1k_prompt: parseFloat(e.target.value)
                          }
                        }
                      })}
                      className="w-full px-3 py-2 border rounded-lg"
                      step="0.001"
                      min="0"
                    />
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Model ID: {config.search_models.perplexity.model_id}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t bg-gray-50 flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={saveConfig}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center disabled:opacity-50"
          >
            <Save className="mr-2" size={18} />
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>

      {showAddModel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Add New Model</h3>
            <div className="mb-4">
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg mb-2"
              >
                <option value="">Select a model...</option>
                {availableModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name} ({model.provider})
                  </option>
                ))}
              </select>
              {selectedModel && (() => {
                const model = availableModels.find(m => m.id === selectedModel);
                return model ? (
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>Context: {model.context_length?.toLocaleString() || 'N/A'} tokens</p>
                    <p>Cost: ${model.pricing?.prompt || 0}/1K prompt, ${model.pricing?.completion || 0}/1K completion</p>
                  </div>
                ) : null;
              })()}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddModel(false);
                  setSelectedModel('');
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={addModel}
                disabled={!selectedModel}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Add Model
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ModelConfig;